import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

import AttachTestsPanel from "@/components/job-workflow/AttachTestsPanel";
import WorkflowConfigPanel from "@/components/job-workflow/WorkflowConfigPanel";
import PanelMembersPanel from "@/components/job-workflow/PanelMembersPanel";
import HiringManagersPanel from "@/components/job-workflow/HiringManagersPanel";
import CandidateReviewPanel from "@/components/job-workflow/CandidateReviewPanel";

export default function JobWorkflowConfigPage() {
  const { jobId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [job, setJob] = useState(null);
  const [allTests, setAllTests] = useState([]);
  const [attachedTests, setAttachedTests] = useState([]);
  const [workflowConfig, setWorkflowConfig] = useState({
    auto_shortlist_enabled: false,
    auto_shortlist_threshold: 70,
    ai_recommendation_enabled: false,
  });
  const [panelMembers, setPanelMembers] = useState([]);
  const [hiringManagers, setHiringManagers] = useState([]);
  const [candidatesPendingReview, setCandidatesPendingReview] = useState([]);
  const [reviewLoading, setReviewLoading] = useState(false);

  const [showAttachTestDialog, setShowAttachTestDialog] = useState(false);
  const [showAddPanelDialog, setShowAddPanelDialog] = useState(false);
  const [showAddHmDialog, setShowAddHmDialog] = useState(false);
  const [selectedTestIds, setSelectedTestIds] = useState([]);
  const [newPanelMember, setNewPanelMember] = useState({
    email: "",
    name: "",
    role: "Technical Interviewer",
    interview_at: "",
    interview_duration_minutes: 60,
    candidate_name: "",
  });
  const [newHiringManager, setNewHiringManager] = useState({ email: "", name: "" });

  const loadCandidatesPendingReview = useCallback(async () => {
    try {
      const res = await api.get(`/admin/jobs/${jobId}/candidates-pending-review`);
      setCandidatesPendingReview(res.data.candidates || []);
    } catch (error) {
      console.error("Failed to load pending reviews:", error);
    }
  }, [jobId]);

  const loadData = useCallback(async () => {
    try {
      const [jobRes, configRes, testsRes, allTestsRes] = await Promise.all([
        api.get(`/admin/jobs/${jobId}`),
        api.get(`/admin/jobs/${jobId}/workflow-config`),
        api.get(`/admin/jobs/${jobId}/attached-tests`),
        api.get(`/admin/tests`),
      ]);
      setJob(jobRes.data);
      setWorkflowConfig(configRes.data.workflow_config);
      setPanelMembers(configRes.data.panel_members || []);
      setHiringManagers(jobRes.data.hiring_managers || []);
      setAttachedTests(testsRes.data.tests || []);
      setAllTests(allTestsRes.data.tests || []);
      loadCandidatesPendingReview();
      setLoading(false);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load job data");
      setLoading(false);
    }
  }, [jobId, loadCandidatesPendingReview]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAttachTests = async () => {
    if (selectedTestIds.length === 0) {
      toast.error("Please select at least one test");
      return;
    }
    try {
      await api.post(`/admin/jobs/${jobId}/attach-tests`, selectedTestIds);
      toast.success("Tests attached successfully");
      setShowAttachTestDialog(false);
      loadData();
    } catch (error) {
      toast.error("Failed to attach tests");
    }
  };

  const handleSaveWorkflow = async () => {
    try {
      await api.post(`/admin/jobs/${jobId}/configure-workflow`, workflowConfig);
      toast.success("Workflow configuration saved");
    } catch (error) {
      toast.error("Failed to save workflow");
    }
  };

  const handleAddPanelMember = async () => {
    if (!newPanelMember.email || !newPanelMember.name) {
      toast.error("Please fill in all fields");
      return;
    }
    try {
      // Convert local datetime-input value to ISO-8601 UTC if provided
      const payload = { ...newPanelMember };
      if (payload.interview_at) {
        payload.interview_at = new Date(payload.interview_at).toISOString();
      } else {
        delete payload.interview_at;
      }
      await api.post(`/admin/jobs/${jobId}/add-panel-member`, payload);
      toast.success("Panel member added");
      setShowAddPanelDialog(false);
      setNewPanelMember({
        email: "",
        name: "",
        role: "Technical Interviewer",
        interview_at: "",
        interview_duration_minutes: 60,
        candidate_name: "",
      });
      loadData();
    } catch (error) {
      toast.error("Failed to add panel member");
    }
  };

  const handleRemovePanelMember = async (email) => {
    try {
      await api.delete(`/admin/jobs/${jobId}/panel-member/${encodeURIComponent(email)}`);
      toast.success("Panel member removed");
      loadData();
    } catch (error) {
      toast.error("Failed to remove panel member");
    }
  };

  const handleAddHiringManager = async () => {
    if (!newHiringManager.email || !newHiringManager.name) {
      toast.error("Please fill in both name and email");
      return;
    }
    try {
      const res = await api.post(`/admin/jobs/${jobId}/add-hiring-manager`, newHiringManager);
      const msg = res.data.email_sent
        ? "Hiring manager added — magic link emailed"
        : "Hiring manager added (SMTP disabled, share the link manually)";
      toast.success(msg);
      setShowAddHmDialog(false);
      setNewHiringManager({ email: "", name: "" });
      loadData();
    } catch (error) {
      const detail = error.response?.data?.detail || "Failed to add hiring manager";
      toast.error(typeof detail === "string" ? detail : "Failed to add hiring manager");
    }
  };

  const handleRemoveHiringManager = async (email) => {
    try {
      await api.delete(`/admin/jobs/${jobId}/hiring-manager/${encodeURIComponent(email)}`);
      toast.success("Hiring manager access revoked");
      loadData();
    } catch (error) {
      toast.error("Failed to revoke hiring manager access");
    }
  };

  const withReviewLoading = async (fn, successMsg, errorMsg) => {
    setReviewLoading(true);
    try {
      await fn();
      toast.success(successMsg);
      loadCandidatesPendingReview();
    } catch (error) {
      toast.error(errorMsg);
    }
    setReviewLoading(false);
  };

  const handleApproveCandidate = (candidateId, notes = "") =>
    withReviewLoading(
      () => api.post(`/admin/candidates/${candidateId}/approve-shortlist`, { notes }),
      "Candidate approved and shortlisted",
      "Failed to approve candidate"
    );

  const handleRejectCandidate = (candidateId, notes = "") =>
    withReviewLoading(
      () => api.post(`/admin/candidates/${candidateId}/reject-shortlist`, { notes }),
      "Candidate rejected",
      "Failed to reject candidate"
    );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6" data-testid="job-workflow-config-page">
      <div className="max-w-5xl mx-auto">
        <div className="mb-6">
          <Button variant="ghost" onClick={() => navigate("/admin/jobs")} className="mb-4" data-testid="back-to-jobs-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Jobs
          </Button>
          <h1 className="text-3xl font-bold text-gray-900">{job?.title}</h1>
          <p className="text-gray-600 mt-1">Configure workflow and attach tests</p>
        </div>

        <AttachTestsPanel
          allTests={allTests}
          attachedTests={attachedTests}
          selectedTestIds={selectedTestIds}
          setSelectedTestIds={setSelectedTestIds}
          showDialog={showAttachTestDialog}
          setShowDialog={setShowAttachTestDialog}
          onAttach={handleAttachTests}
        />

        <WorkflowConfigPanel
          config={workflowConfig}
          setConfig={setWorkflowConfig}
          onSave={handleSaveWorkflow}
        />

        <PanelMembersPanel
          members={panelMembers}
          newMember={newPanelMember}
          setNewMember={setNewPanelMember}
          showDialog={showAddPanelDialog}
          setShowDialog={setShowAddPanelDialog}
          onAdd={handleAddPanelMember}
          onRemove={handleRemovePanelMember}
        />

        <HiringManagersPanel
          members={hiringManagers}
          newMember={newHiringManager}
          setNewMember={setNewHiringManager}
          showDialog={showAddHmDialog}
          setShowDialog={setShowAddHmDialog}
          onAdd={handleAddHiringManager}
          onRemove={handleRemoveHiringManager}
        />

        {workflowConfig.ai_recommendation_enabled && (
          <CandidateReviewPanel
            candidates={candidatesPendingReview}
            loading={reviewLoading}
            onApprove={handleApproveCandidate}
            onReject={handleRejectCandidate}
          />
        )}
      </div>
    </div>
  );
}
